import React, { useState } from 'react';
import { TextField, Autocomplete, Chip, Box, Avatar, Typography } from '@mui/material';
import CrudPage from '../../components/CrudPage';
import {
  getCategoriesApi, addCategoryApi, editCategoryApi, deleteCategoryApi,
  getShowsApi,
} from '../../services/api';
import { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/deplbr8mp/image/upload';
const UPLOAD_PRESET = 'jamsap-upload';

const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('upload_preset', UPLOAD_PRESET);
  const res = await fetch(CLOUDINARY_URL, { method: 'POST', body: fd });
  const data = await res.json();
  return data.url;
};

const DEFAULT_FORM = { title: '', description: '', bannerImage: '', iconImage: '', shows: [] };

function CategoryFormFields({ form, setForm }) {
  const [shows, setShows] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getShowsApi().then(({ data }) => setShows(data.rows || [])).catch(() => {});
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, bannerImage: url }));
    } catch { } finally { setUploading(false); }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          variant="rounded"
          src={form.bannerImage || ''}
          sx={{ width: 80, height: 80 }}
        />
        <Box>
          <Typography variant="body2" fontWeight={600} mb={0.5}>Banner Image</Typography>
          <label htmlFor="cat-banner-upload">
            <input id="cat-banner-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            <Typography
              component="span"
              variant="caption"
              sx={{ cursor: 'pointer', color: '#fec958', textDecoration: 'underline' }}
            >
              {uploading ? 'Uploading…' : 'Upload Image'}
            </Typography>
          </label>
        </Box>
      </Box>

      <TextField
        label="Title" value={form.title} required size="small"
        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        fullWidth
      />
      <TextField
        label="Description" value={form.description} size="small" multiline rows={3}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        fullWidth
      />

      <Autocomplete
        multiple
        options={shows}
        getOptionLabel={(o) => o.title || ''}
        value={shows.filter((s) => (form.shows || []).some((sel) => (sel._id || sel) === s._id))}
        onChange={(_, v) => setForm((f) => ({ ...f, shows: v }))}
        isOptionEqualToValue={(o, v) => o._id === (v._id || v)}
        renderTags={(val, getTagProps) =>
          val.map((opt, i) => <Chip key={opt._id} label={opt.title} size="small" {...getTagProps({ index: i })} />)
        }
        renderInput={(params) => <TextField {...params} label="Select Shows" size="small" />}
      />
    </>
  );
}

const COLUMNS = [
  {
    field: 'bannerImage', headerName: '', width: 60, sortable: false,
    renderCell: (p) => <Avatar variant="rounded" src={p.value} sx={{ width: 36, height: 36, marginTop: 1 }} />,
  },
  { field: 'title', headerName: 'Title', flex: 1 },
  { field: 'description', headerName: 'Description', flex: 2 },
  {
    field: 'shows', headerName: 'Shows', width: 100,
    renderCell: (p) => <Chip label={p.value?.length ?? 0} size="small" />,
  },
  {
    field: 'createdAt', headerName: 'Created', width: 150,
    renderCell: (p) => p.value ? new Date(p.value).toLocaleDateString() : '—',
  },
];

export default function Categories() {
  const { user } = useAuth();
  const canWrite = !user?.adminAccess?.categories || user.adminAccess.categories.write === true;

  return (
    <CrudPage
      title="Categories"
      subtitle="Manage content categories"
      columns={COLUMNS}
      fetchFn={getCategoriesApi}
      addFn={addCategoryApi}
      editFn={editCategoryApi}
      deleteFn={deleteCategoryApi}
      FormFields={CategoryFormFields}
      defaultFormState={DEFAULT_FORM}
      canWrite={canWrite}
      formToPayload={(f) => ({
        title: f.title,
        description: f.description,
        bannerImage: f.bannerImage,
        iconImage: f.iconImage,
        shows: f.shows.map((s) => s._id || s),
      })}
    />
  );
}
